import {
    Groups,
    groupsLeftNav_ShowLessButton,
    groupsLeftNav_ShowMoreButton,
} from './GroupList.locstring.json';
import loc from 'owa-localize';
import GroupListNode from './GroupListNode';
import toggleIsShowingAllGroups from '../mutators/toggleIsShowingAllGroups';
import { getLeftNavGroupsStore } from '../store/store';
import { MAX_GROUP_NODES_WHEN_COLLAPSED } from '../utils/constants';
import { observer } from 'mobx-react';
import { AriaProperties, AriaRoles, generateDomPropertiesForAria } from 'owa-accessibility';
import { logMountingPerformance, logUsage } from 'owa-analytics';
import LeftNavGroupsContextMenuSchema from 'owa-group-left-nav/lib/store/schema/LeftNavGroupsContextMenu';
import { getGroupsStore } from 'owa-groups-shared-store';
import GroupInformation from 'owa-groups-shared-store/lib/schema/GroupInformation';
import UnifiedGroup from 'owa-service/lib/contract/UnifiedGroup';

import { ChevronProps } from 'owa-tree-node/lib/components/TreeNode';
import TreeNode from 'owa-tree-node/lib/components/TreeNode';
import Droppable from 'owa-dnd/lib/components/Droppable';
import createDropViewState from 'owa-dnd/lib/utils/createDropViewState';
import { DragData } from 'owa-dnd/lib/utils/dragDataUtil';
import DropEffect from 'owa-dnd/lib/store/schema/DropEffect';
import * as React from 'react';
export interface GroupListProps extends React.HTMLProps<HTMLDivElement> {
    selectGroup: (groupId: string, customData: any) => void; // select group callback when a node is selected.
    isSelected: (groupId: string, customData: any) => boolean; // Callback to determine if a given node is selected
    isGroupsTreeCollapsed: boolean;
    toggleGroupTreeExpansion: () => void;
    renderRightCharm: (groupId: string, customData: any) => JSX.Element;
    renderRightCharmHover: (groupId: string, customData: any) => JSX.Element;
    onChildNodeContextMenuOpened: (evt: React.MouseEvent<unknown>, group: GroupInformation) => void; // Callback to be called to open the Child Nodes context menu
    renderGroupListCustomNodes: () => JSX.Element[];
    canDropOnListNode?: (dragData: DragData) => DropEffect;
    customData?: any;
    onListNodeDrop?: (dragData: DragData, groupId: string) => void;
    onRootNodeContextMenuOpened?: (evt: React.MouseEvent<unknown>) => void; // Callback to be called to open the group root node context menu
    renderChildNodeContextMenu?: (
        withContextMenuGroup: LeftNavGroupsContextMenuSchema
    ) => JSX.Element; // The context menu that should be rendered when right clicking on a Child Node
    renderRootNodeContextMenu?: (
        withContextMenuGroup: LeftNavGroupsContextMenuSchema
    ) => JSX.Element; // The context menu that should be rendered when right clicking on the group root node
    renderRootNodeRightCharm?: () => JSX.Element;
    renderRootNodeRightCharmHover?: () => JSX.Element;
    onMouseEnterShowMoreButton?: () => void;
    onShowMoreEvent?: () => void;
}

const groupsStore = getGroupsStore();
const leftNavGroupsStore = getLeftNavGroupsStore();

/**
 * Group list that renders a root node and all groups supplied to it and their children.
 */
@observer
@logMountingPerformance('GroupList')
export default class GroupList extends React.Component<GroupListProps, {}> {
    private isDragOverExpandCollapseNode = false;

    render() {
        const groupIds = leftNavGroupsStore.myOrgGroups;
        const ariaProps: AriaProperties = {
            role: AriaRoles.tree,
        };
        const isGroupsTreeCollapsed = this.props.isGroupsTreeCollapsed;

        return (
            <div
                style={this.props.style}
                className={this.props.className}
                {...generateDomPropertiesForAria(ariaProps)}>
                {this.renderGroupRoot(isGroupsTreeCollapsed)}
                {// Render all the nodes starting with the root node
                this.renderChildren(groupIds, isGroupsTreeCollapsed)}
                {// Render the context menu if it is set to be visible
                leftNavGroupsStore.withContextMenuGroup &&
                    (leftNavGroupsStore.withContextMenuGroup.isRootNode
                        ? this.props.renderRootNodeContextMenu &&
                          this.props.renderRootNodeContextMenu(
                              leftNavGroupsStore.withContextMenuGroup
                          )
                        : this.props.renderChildNodeContextMenu &&
                          this.props.renderChildNodeContextMenu(
                              leftNavGroupsStore.withContextMenuGroup
                          ))}
                {groupIds.length > MAX_GROUP_NODES_WHEN_COLLAPSED &&
                    this.renderCollapseExpandNode(isGroupsTreeCollapsed)}
                {this.props.renderGroupListCustomNodes && this.props.renderGroupListCustomNodes()}
            </div>
        );
    }

    private renderNode(group: UnifiedGroup): JSX.Element {
        return (
            <GroupListNode
                selectGroup={this.props.selectGroup}
                isSelected={this.props.isSelected}
                renderRightCharm={this.props.renderRightCharm}
                renderRightCharmHover={this.props.renderRightCharmHover}
                displayName={group.DisplayName}
                groupId={group.SmtpAddress}
                key={group.SmtpAddress}
                onContextMenu={this.props.onChildNodeContextMenuOpened}
                customData={this.props.customData}
                onListNodeDrop={this.props.onListNodeDrop}
                canDropOnListNode={this.props.canDropOnListNode}
            />
        );
    }

    private onRootNodeChevronClicked = (evt: React.MouseEvent<unknown> | KeyboardEvent) => {
        evt.stopPropagation();
        this.props.toggleGroupTreeExpansion();
    };

    private renderGroupRoot(isGroupsTreeCollapsed: boolean): JSX.Element {
        const chevronProps: ChevronProps = {
            isExpanded: !isGroupsTreeCollapsed,
            onClick: this.onRootNodeChevronClicked,
        };

        return (
            <TreeNode
                chevronProps={chevronProps}
                depth={0}
                displayName={loc(Groups)}
                isRootNode={true}
                key={'Groups_root'}
                onClick={chevronProps.onClick}
                onContextMenu={this.onRootContextMenu}
                renderRightCharm={this.props.renderRootNodeRightCharm}
                renderRightCharmHover={this.props.renderRootNodeRightCharmHover}
            />
        );
    }

    private onRootContextMenu = (evt: React.MouseEvent<unknown>) => {
        const { onRootNodeContextMenuOpened } = this.props;

        if (onRootNodeContextMenuOpened) {
            evt.stopPropagation();
            evt.preventDefault();
            onRootNodeContextMenuOpened(evt);
        }
    };

    private renderChildren(myOrgGroups: string[], isGroupsTreeCollapsed: boolean): JSX.Element[] {
        const renderedNodes: JSX.Element[] = [];

        if (isGroupsTreeCollapsed) {
            return renderedNodes;
        }

        /*
            We either show the top 5 Groups in Prankie order or all Groups sorted by DisplayName
        */
        if (leftNavGroupsStore.shouldShowAllGroups) {
            const unsortedGroups: GroupInformation[] = [];
            myOrgGroups.forEach(groupId => {
                const group = groupsStore.groups.get(groupId);
                unsortedGroups.push(group);
            });
            unsortedGroups
                .sort((group1: GroupInformation, group2: GroupInformation) => {
                    return group1.basicInformation.DisplayName.localeCompare(
                        group2.basicInformation.DisplayName
                    );
                })
                .forEach(group => {
                    renderedNodes.push(this.renderNode(group.basicInformation));
                });
        } else {
            myOrgGroups.slice(0, MAX_GROUP_NODES_WHEN_COLLAPSED).forEach(groupId => {
                const group = groupsStore.groups.get(groupId);
                renderedNodes.push(this.renderNode(group.basicInformation));
            });
        }
        return renderedNodes;
    }

    private renderCollapseExpandNode(isGroupsTreeCollapsed: boolean): any {
        if (isGroupsTreeCollapsed) {
            return null;
        }

        const nodeDisplayName = leftNavGroupsStore.shouldShowAllGroups
            ? loc(groupsLeftNav_ShowLessButton)
            : loc(groupsLeftNav_ShowMoreButton);

        const emptyDropViewState = createDropViewState();

        return (
            <Droppable
                dropViewState={emptyDropViewState}
                onDrop={this.onDropOnCollapseExpandNode}
                canDrop={this.canDropOnCollapseExpandNode}
                onDragOver={this.onDragOverCollapseExpandNode}
                onDragLeave={this.onDragLeaveCollapseExpandNode}>
                <TreeNode
                    displayName={nodeDisplayName}
                    isCustomActionNode={true}
                    isRootNode={false}
                    key={'groupsCollapseExpandNode'}
                    onClick={this.onCollapseExpandNodeClick}
                    onContextMenu={this.noContextMenu}
                    onMouseEnter={this.props.onMouseEnterShowMoreButton}
                />
            </Droppable>
        );
    }

    private onDropOnCollapseExpandNode = () => {
        // No-Op
    };

    private canDropOnCollapseExpandNode = (dragData: DragData) => {
        if (!leftNavGroupsStore.shouldShowAllGroups && this.props.canDropOnListNode) {
            return this.props.canDropOnListNode(dragData);
        }

        return false;
    };

    private onDragOverCollapseExpandNode = (dragData: DragData) => {
        this.isDragOverExpandCollapseNode = true;

        setTimeout(() => {
            if (this.isDragOverExpandCollapseNode && !getLeftNavGroupsStore().shouldShowAllGroups) {
                this.toggleCollapseExpandNode();
            }
        }, 1000);
    };

    private onDragLeaveCollapseExpandNode = () => {
        this.isDragOverExpandCollapseNode = false;
    };

    private noContextMenu = (evt: React.MouseEvent<unknown>) => {
        evt.stopPropagation();
        evt.preventDefault();
    };

    private onCollapseExpandNodeClick = (evt: React.MouseEvent<unknown> | KeyboardEvent) => {
        evt.stopPropagation();
        this.toggleCollapseExpandNode();
    };

    private toggleCollapseExpandNode = () => {
        const joinedGroupCount = leftNavGroupsStore.myOrgGroups.length;
        logUsage('GroupList_ToggleShouldShowAllGroups', {
            isShowMore: !leftNavGroupsStore.shouldShowAllGroups, // true if the user clicked on "More" button to show all groups, false if the user clicked on "Less" to show less groups
            joinedGroupCount: joinedGroupCount >= 21 ? '21+' : joinedGroupCount, // Number of groups the user would see when showing all groups. For more than 20 groups, we won't log the actual count so that we limit the unique values for this metric due to reporting limitations
        });

        if (this.props.onShowMoreEvent) {
            this.props.onShowMoreEvent();
        }

        toggleIsShowingAllGroups();
    };
}
